import type { BuyerFormData } from '../components/BuyerForm';
import type { UpdateBuyerInput } from '../hooks/useUpdateBuyer';

/**
 * Address-related fields on the buyer form.
 *
 * When NONE of these differ from the loaded buyer we must send the backend's
 * supported "simple update" shape with no address keys at all. choco-backend
 * `updateBuyer.ts:59-78` makes a LIVE Google Place Details call for the stored
 * `googlePlaceId` on every save whose payload carries an address, then
 * overwrites the payload's components (street/city/country/…) from that place
 * (`buyer = {...buyer, ...address}`, lines 72-77). A retired place id throws a
 * generic 500. Omitting the keys skips that round-trip entirely.
 */
const ADDRESS_FIELDS: readonly (keyof BuyerFormData)[] = [
  'fullAddress',
  'addressCity',
  'addressCountry',
  'addressLatitude',
  'addressLongitude',
  'addressUTCOffset',
  'addressSource',
  'addressGooglePlaceId',
  'addressGoogleAddressId',
];

/**
 * True when at least one address-related field differs from the loaded buyer's
 * initial form values. Drives whether the update payload carries address keys.
 */
export const hasAddressChanged = (
  data: BuyerFormData,
  initial: Partial<BuyerFormData>
): boolean =>
  ADDRESS_FIELDS.some((field) => {
    const next = data[field] ?? undefined;
    const previous = initial[field] ?? undefined;
    return next !== previous;
  });

/**
 * True when the save moves the buyer off a Google-sourced address and onto a
 * custom one.
 *
 * This is the one transition that must clear state rather than merge over it.
 * `updateBuyerService` persists `{...existingBuyer, ...buyer}` and the dynamo
 * client only emits a `REMOVE` for a key whose value is explicitly `null`
 * (`updateItemInputFactory.buildUpdateExpression`: `undefined` is skipped,
 * `null` is removed). So omitting the place ids leaves them on the record: the
 * buyer keeps matching supplier invites on a Google place it no longer claims,
 * and `isGooglePlaceIdDuplicated` keeps 409-blocking any other buyer that picks
 * that place. The establishment id goes: the buyer is no longer identified by a
 * Google place. The address id is re-pointed rather than dropped, because the
 * custom pick supplies one and it describes the address the buyer now has.
 *
 * The reverse transition needs no clearing: a custom→google save carries the new
 * `googlePlaceId`, and any leftover `googlePlaceAddressId` is re-derived by the
 * same lambda because the establishment id changed
 * (`calculateWhichGooglePlaceIdsShouldBeUpdated`).
 */
const isGoogleToCustomSwitch = (
  data: BuyerFormData,
  initial: Partial<BuyerFormData>
): boolean =>
  initial.addressSource === 'google' && data.addressSource === 'custom';

/**
 * Builds the `buyerUpdate` payload for the buyer profile edit form.
 *
 * Address keys (fullAddress, addressSource, coordinates, city/country, place
 * ids) are included ONLY when an address field actually changed. On unrelated
 * edits (name, display name, delivery comment, isTest) the payload carries no
 * address keys, so the backend skips the live Google Place Details lookup and
 * component overwrite (updateBuyer.ts:72-77) — eliminating the per-save Google
 * round-trip and the dead-place-id 500 for edits that never touched the
 * address. `fullAddress` and `addressSource` are only ever emitted together,
 * matching the backend validator's `.with` pairing (validator.ts:122-123).
 *
 * Coordinates use `??`, not `||`: `0` is a legal latitude, longitude and
 * `utcOffset`. Dropping a `0` longitude while keeping its latitude would trip
 * the validator's `.with('longitude', 'latitude')` peer binding and 400 the
 * whole save.
 */
export const buildBuyerUpdateInput = (
  buyerId: string,
  data: BuyerFormData,
  initial: Partial<BuyerFormData>
): UpdateBuyerInput => {
  const base: UpdateBuyerInput = {
    id: buyerId,
    name: data.name,
    displayName: data.displayName || undefined,
    deliveryComment: data.deliveryComment || undefined,
    isTest: data.isTest,
  };

  if (!hasAddressChanged(data, initial)) {
    return base;
  }

  const clearsGooglePlaceIds = isGoogleToCustomSwitch(data, initial);

  return {
    ...base,
    fullAddress: data.fullAddress || undefined,
    addressSource: data.addressSource || 'custom',
    addressCity: data.addressCity || undefined,
    addressCountry: data.addressCountry || undefined,
    addressLatitude: data.addressLatitude ?? undefined,
    addressLongitude: data.addressLongitude ?? undefined,
    addressUTCOffset: data.addressUTCOffset ?? undefined,
    addressGooglePlaceId: clearsGooglePlaceIds
      ? null
      : data.addressGooglePlaceId || undefined,
    // The picked id wins. Only a switch that leaves no id behind sends the null
    // that clears the stored one; otherwise this would discard the id the user's
    // own pick just supplied and leave the record wrong until the next save.
    addressGoogleAddressId:
      data.addressGoogleAddressId || (clearsGooglePlaceIds ? null : undefined),
  };
};
